import { useState } from 'react';
import { FormFieldDef, InputType } from '../../../interfaces/form-config.interface';
import { Segment, SegmentGroup, toSegments, flattenSegments } from './form-config.utils';

// ── Props ──────────────────────────────────────────────────────────────────

export interface FieldTableProps {
  fields: FormFieldDef[];
  selectedKey: string | null;
  dirtyKeys: Set<string>;
  onSelectField: (key: string) => void;
  onReorder: (reorderedFields: FormFieldDef[]) => void;
}

// ── DragHandle ─────────────────────────────────────────────────────────────

const DragHandle = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    {...props}
    title="Drag to reorder"
    className="px-2 py-3 flex items-center cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400 select-none shrink-0 touch-none"
  >
    <svg width="12" height="20" viewBox="0 0 12 20" fill="currentColor">
      <circle cx="3" cy="4" r="1.5" />
      <circle cx="9" cy="4" r="1.5" />
      <circle cx="3" cy="10" r="1.5" />
      <circle cx="9" cy="10" r="1.5" />
      <circle cx="3" cy="16" r="1.5" />
      <circle cx="9" cy="16" r="1.5" />
    </svg>
  </div>
);

// ── Type badge color map ───────────────────────────────────────────────────

function typeBadgeClass(inputType: InputType): string {
  switch (inputType) {
    case InputType.TEXTBOX:
    case InputType.TEXTAREA:
    case InputType.DYNAMIC_TEXTBOX:
      return 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300';
    case InputType.DYNAMIC_CHECK_TEXTBOX:
      return 'bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300';
    case InputType.NUMERIC_TEXTBOX:
      return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300';
    case InputType.PRICE_CALENDAR:
    case InputType.DURATION_DYNAMIC:
      return 'bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-300';
    case InputType.TOGGLE:
    case InputType.TOGGLE_SPECIAL:
      return 'bg-orange-50 text-orange-700 dark:bg-orange-950 dark:text-orange-300';
    case InputType.DROPDOWN:
      return 'bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-300';
    case InputType.SELECTABLE_PILL:
    case InputType.MULTI_SELECTABLE_PILL:
      return 'bg-pink-50 text-pink-700 dark:bg-pink-950 dark:text-pink-300';
    case InputType.IMAGE_VIDEO_UPLOADER:
    case InputType.IMAGE_UPLOADER:
    case InputType.FILE_UPLOADER:
      return 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300';
    case InputType.SEARCH_LOCATION:
    case InputType.SEARCH_COUNTRY:
      return 'bg-teal-50 text-teal-700 dark:bg-teal-950 dark:text-teal-300';
    default:
      return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
  }
}

function typeLabel(inputType: InputType): string {
  return inputType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── FieldRowItem ───────────────────────────────────────────────────────────

interface FieldRowItemProps {
  field: FormFieldDef;
  isSelected: boolean;
  isDirty: boolean;
  isDragging: boolean;
  isDragOver: boolean;
  indented?: boolean;
  onSelect: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}

const FieldRowItem = ({
  field,
  isSelected,
  isDirty,
  isDragging,
  isDragOver,
  indented,
  onSelect,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}: FieldRowItemProps) => {
  const isInactive = field.isActive === false;
  const isLocked = field.layoutFixed || field.detailFixed;

  const rowClass = [
    'flex items-center w-full text-left border-b border-gray-100 dark:border-gray-800 transition-colors group',
    isSelected
      ? 'bg-blue-50 dark:bg-blue-950/30 border-l-2 border-l-blue-500'
      : isDirty
      ? 'border-l-2 border-l-yellow-400'
      : '',
    isDragOver ? 'border-t-2 border-t-blue-500' : '',
    isDragging ? 'opacity-40' : '',
    isInactive ? 'opacity-45' : '',
    'hover:bg-blue-50/50 dark:hover:bg-blue-950/20 cursor-pointer',
    indented ? 'pl-4' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={rowClass}
      draggable
      onClick={onSelect}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {/* Drag handle */}
      <DragHandle onClick={(e) => e.stopPropagation()} />

      {/* Key / Labels */}
      <div className="flex-1 min-w-0 py-2 pr-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="font-mono text-xs text-gray-800 dark:text-gray-200 truncate">
            {field.key}
          </span>
          {isDirty && (
            <span
              className="inline-block w-1.5 h-1.5 rounded-full bg-yellow-400 shrink-0"
              title="Unsaved changes"
            />
          )}
          {isLocked && (
            <span className="text-gray-400 text-[10px] shrink-0" title="Layout/detail fixed">
              🔒
            </span>
          )}
        </div>
        <div className="text-[11px] text-gray-400 dark:text-gray-500 truncate mt-0.5">
          {field.label}
        </div>
      </div>

      {/* Type badge */}
      <div className="w-32 hidden sm:flex shrink-0 px-2">
        <span
          className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium truncate max-w-full ${typeBadgeClass(field.inputType)}`}
        >
          {typeLabel(field.inputType)}
        </span>
      </div>

      {/* Sections */}
      <div className="w-40 hidden md:flex flex-col gap-0.5 shrink-0 px-2">
        {field.listingSection || field.detailSection ? (
          <>
            {field.listingSection && (
              <span className="text-[10px] text-blue-600 dark:text-blue-400 truncate">
                L: {field.listingSection}
              </span>
            )}
            {field.detailSection && (
              <span className="text-[10px] text-blue-600 dark:text-blue-400 truncate">
                D: {field.detailSection}
              </span>
            )}
          </>
        ) : (
          <span className="text-[10px] text-gray-300 dark:text-gray-600">—</span>
        )}
      </div>

      {/* Flags */}
      <div className="w-28 hidden lg:flex flex-wrap gap-1 shrink-0 px-2 py-1">
        {field.required && (
          <span className="px-1 py-0 rounded text-[9px] font-semibold bg-red-100 text-red-600 dark:bg-red-950 dark:text-red-400">
            req
          </span>
        )}
        {field.visible && (
          <span className="px-1 py-0 rounded text-[9px] font-semibold bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
            vis
          </span>
        )}
        {field.isFilterable && (
          <span className="px-1 py-0 rounded text-[9px] font-semibold bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
            flt
          </span>
        )}
        {field.isSortable && (
          <span className="px-1 py-0 rounded text-[9px] font-semibold bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
            srt
          </span>
        )}
        {field.isSearchable && (
          <span className="px-1 py-0 rounded text-[9px] font-semibold bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
            src
          </span>
        )}
        {field.allowMultiple && (
          <span className="px-1 py-0 rounded text-[9px] font-semibold bg-amber-100 text-amber-600 dark:bg-amber-950 dark:text-amber-400">
            multi
          </span>
        )}
        {isInactive && (
          <span className="px-1 py-0 rounded text-[9px] font-semibold bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500">
            inactive
          </span>
        )}
        {field.listingColumn && (
          <span className="px-1 py-0 rounded text-[9px] font-semibold bg-sky-100 text-sky-600 dark:bg-sky-950 dark:text-sky-400">
            col
          </span>
        )}
        {field.visibleWhen && (
          <span
            className="px-1 py-0 rounded text-[9px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400"
            title={`visible when: ${field.visibleWhen}`}
          >
            when
          </span>
        )}
      </div>

      {/* Chevron */}
      <div className="w-8 flex items-center justify-center shrink-0 text-gray-300 dark:text-gray-600 group-hover:text-gray-400 dark:group-hover:text-gray-500">
        <svg width="8" height="12" viewBox="0 0 8 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="1 1 7 6 1 11" />
        </svg>
      </div>
    </div>
  );
};

// ── GroupBlock ─────────────────────────────────────────────────────────────

interface GroupBlockProps {
  seg: SegmentGroup;
  selectedKey: string | null;
  dirtyKeys: Set<string>;
  isSegDragging: boolean;
  isSegDragOver: boolean;
  onSelectField: (key: string) => void;
  onSegDragStart: (e: React.DragEvent) => void;
  onSegDragEnd: () => void;
  onSegDragOver: (e: React.DragEvent) => void;
  onSegDrop: (e: React.DragEvent) => void;
  onGroupReorder: (groupName: string, reorderedFields: FormFieldDef[]) => void;
}

const GroupBlock = ({
  seg,
  selectedKey,
  dirtyKeys,
  isSegDragging,
  isSegDragOver,
  onSelectField,
  onSegDragStart,
  onSegDragEnd,
  onSegDragOver,
  onSegDrop,
  onGroupReorder,
}: GroupBlockProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const [grpDragSrc, setGrpDragSrc] = useState<number | null>(null);
  const [grpDragOver, setGrpDragOver] = useState<number | null>(null);

  const handleGrpDragStart = (idx: number) => (e: React.DragEvent) => {
    e.stopPropagation();
    setGrpDragSrc(idx);
  };
  const handleGrpDragEnd = () => {
    setGrpDragSrc(null);
    setGrpDragOver(null);
  };
  const handleGrpDragOver = (idx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setGrpDragOver(idx);
  };
  const handleGrpDrop = (dropIdx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (grpDragSrc === null || grpDragSrc === dropIdx) {
      setGrpDragSrc(null);
      setGrpDragOver(null);
      return;
    }
    const reordered = [...seg.fields];
    const [moved] = reordered.splice(grpDragSrc, 1);
    reordered.splice(dropIdx, 0, moved);
    onGroupReorder(seg.name, reordered);
    setGrpDragSrc(null);
    setGrpDragOver(null);
  };

  return (
    <div
      className={[
        'border-l-4 border-purple-300 dark:border-purple-700 transition-all',
        isSegDragging ? 'opacity-40' : '',
        isSegDragOver ? 'border-t-2 border-t-blue-500' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onDragOver={onSegDragOver}
      onDrop={onSegDrop}
    >
      {/* Group header */}
      <div
        className="flex items-center gap-2 pl-2 pr-3 py-1.5 cursor-grab active:cursor-grabbing bg-purple-50/60 dark:bg-purple-950/20 border-b border-purple-100 dark:border-purple-900"
        draggable
        onDragStart={onSegDragStart}
        onDragEnd={onSegDragEnd}
      >
        <svg
          width="10"
          height="16"
          viewBox="0 0 12 20"
          fill="currentColor"
          className="text-purple-400 dark:text-purple-500 shrink-0"
        >
          <circle cx="3" cy="4" r="1.5" />
          <circle cx="9" cy="4" r="1.5" />
          <circle cx="3" cy="10" r="1.5" />
          <circle cx="9" cy="10" r="1.5" />
          <circle cx="3" cy="16" r="1.5" />
          <circle cx="9" cy="16" r="1.5" />
        </svg>
        <span className="flex-1 text-xs font-semibold text-purple-700 dark:text-purple-300 truncate">
          {seg.name}
        </span>
        <span className="text-[10px] text-purple-400 dark:text-purple-500 shrink-0">
          {seg.fields.length} field{seg.fields.length !== 1 ? 's' : ''}
        </span>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="text-purple-400 hover:text-purple-600 dark:hover:text-purple-300 text-[10px] px-1 select-none"
        >
          {collapsed ? '▼' : '▲'}
        </button>
      </div>

      {/* Group fields */}
      {!collapsed &&
        seg.fields.map((field, idx) => (
          <FieldRowItem
            key={field.key}
            field={field}
            isSelected={selectedKey === field.key}
            isDirty={dirtyKeys.has(field.key)}
            isDragging={grpDragSrc === idx}
            isDragOver={grpDragOver === idx}
            indented
            onSelect={() => onSelectField(field.key)}
            onDragStart={handleGrpDragStart(idx)}
            onDragEnd={handleGrpDragEnd}
            onDragOver={handleGrpDragOver(idx)}
            onDrop={handleGrpDrop(idx)}
          />
        ))}
    </div>
  );
};

// ── FieldTable ─────────────────────────────────────────────────────────────

export const FieldTable = ({
  fields,
  selectedKey,
  dirtyKeys,
  onSelectField,
  onReorder,
}: FieldTableProps) => {
  const [segDragSrc, setSegDragSrc] = useState<number | null>(null);
  const [segDragOver, setSegDragOver] = useState<number | null>(null);

  const segments = toSegments(fields);

  const handleSegDragStart = (idx: number) => (e: React.DragEvent) => {
    e.stopPropagation();
    setSegDragSrc(idx);
  };
  const handleSegDragEnd = () => {
    setSegDragSrc(null);
    setSegDragOver(null);
  };
  const handleSegDragOver = (idx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    setSegDragOver(idx);
  };
  const handleSegDrop = (dropIdx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (segDragSrc === null || segDragSrc === dropIdx) {
      setSegDragSrc(null);
      setSegDragOver(null);
      return;
    }
    const reordered = [...segments];
    const [moved] = reordered.splice(segDragSrc, 1);
    reordered.splice(dropIdx, 0, moved);
    onReorder(flattenSegments(reordered));
    setSegDragSrc(null);
    setSegDragOver(null);
  };

  const handleGroupReorder = (groupName: string, reorderedGroupFields: FormFieldDef[]) => {
    const updated: Segment[] = segments.map((seg) =>
      seg.kind === 'group' && seg.name === groupName
        ? { kind: 'group', name: groupName, fields: reorderedGroupFields }
        : seg,
    );
    onReorder(flattenSegments(updated));
  };

  if (fields.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-gray-400 dark:text-gray-600">
        No fields for this step.
      </div>
    );
  }

  return (
    <div className="overflow-auto">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 flex items-center bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 text-[10px] uppercase font-semibold tracking-wide text-gray-400 dark:text-gray-500 select-none">
        {/* Drag handle column placeholder */}
        <div className="w-8 shrink-0" />
        {/* Key / Labels */}
        <div className="flex-1 py-2 pr-2">Key / Labels</div>
        {/* Type */}
        <div className="w-32 hidden sm:block shrink-0 px-2 py-2">Type</div>
        {/* Sections */}
        <div className="w-40 hidden md:block shrink-0 px-2 py-2">Sections</div>
        {/* Flags */}
        <div className="w-28 hidden lg:block shrink-0 px-2 py-2">Flags</div>
        {/* Chevron placeholder */}
        <div className="w-8 shrink-0" />
      </div>

      {/* Rows */}
      <div>
        {segments.map((seg, idx) => {
          if (seg.kind === 'field') {
            return (
              <FieldRowItem
                key={seg.field.key}
                field={seg.field}
                isSelected={selectedKey === seg.field.key}
                isDirty={dirtyKeys.has(seg.field.key)}
                isDragging={segDragSrc === idx}
                isDragOver={segDragOver === idx}
                onSelect={() => onSelectField(seg.field.key)}
                onDragStart={handleSegDragStart(idx)}
                onDragEnd={handleSegDragEnd}
                onDragOver={handleSegDragOver(idx)}
                onDrop={handleSegDrop(idx)}
              />
            );
          }

          return (
            <GroupBlock
              key={seg.name}
              seg={seg}
              selectedKey={selectedKey}
              dirtyKeys={dirtyKeys}
              isSegDragging={segDragSrc === idx}
              isSegDragOver={segDragOver === idx}
              onSelectField={onSelectField}
              onSegDragStart={handleSegDragStart(idx)}
              onSegDragEnd={handleSegDragEnd}
              onSegDragOver={handleSegDragOver(idx)}
              onSegDrop={handleSegDrop(idx)}
              onGroupReorder={handleGroupReorder}
            />
          );
        })}
      </div>
    </div>
  );
};

export default FieldTable;
